import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useToast } from '@morressier/alembic';
import { zodResolver } from '@hookform/resolvers/zod';
import { emailContentSchema } from '@publishing-engine/domain/validation';
import { Overlay } from 'src/components/Overlay';
import ConfirmInviteModal from '../InviteReviewer/ConfirmInviteModal';
import ConfirmInviteExitModal from '../InviteReviewer/ConfirmInviteExitModal';
import useCurrentContextId, {
  useCurrentOrgId
} from 'src/hooks/useCurrentContextId';
import { ensureUser, fetchOrganization } from 'src/api/queries';
import {
  useInviteReviewer,
  useRequestReviewInRevision
} from 'src/api/commands';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import AddReviewerWithSearch from './AddReviewerWithSearch';
import { useQuery } from '@tanstack/react-query';
import { IBlockedReviewer } from '@publishing-engine/domain/views';
import { EmailContentForm } from '../../../EmailModule';
import { IEmailCommand } from '@publishing-engine/domain/types/email-template';
import { normalizeRecipients } from '../../../../utils/normalizeEmailRecipients';
import { getAllTemplatesByCommand } from '../../../../api/email-service';
import ReviewerInvitationEmailSelection from './ReviewerInvitationEmailSelection';
import { useReviewerInviteList } from '../../../../contexts/ReviewerInviteListProvider';
import { IEmailFormType } from '../../../EmailModule/EditEmailTemplate';
import MultiStepFooter from '../../../MultiStepFooter';
import { Box } from '@chakra-ui/react';
import { useSendReviewerInvitations } from '../../../../hooks/useSendReviewerInvitation';
import { scrollToFirstError } from '../../../../utils/scrollToFirstError';
import NavbarDashboard from 'src/components/NavbarDashboard';
import { useSubmissionContextData } from '../../../../contexts/SubmissionContextProvider';

interface Props {
  closeModal: () => void;
  submissionId: string;
  blockedReviewers?: IBlockedReviewer[];
}

const RsInviteModal = ({
  closeModal,
  submissionId,
  blockedReviewers = []
}: Props) => {
  // state
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showExitConfirmModal, setShowExitConfirmModal] =
    useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(1);

  // hooks
  const toast = useToast();
  const submissionContextId = useCurrentContextId() ?? '';
  const { availableActions } = useSubmissionData();
  const { submissionContext } = useSubmissionContextData();
  const isReviewerAccountCreationEnabled =
    submissionContext?.config.cms?.lexicon?.reviewerActionCard?.createAccount ??
    false;

  const { reviewerList, selectedReviewersToInvite, markReviewersInvited } =
    useReviewerInviteList();
  const confirmedReviewers = reviewerList.filter(
    r => selectedReviewersToInvite[r.email] && !r.invited
  );

  const reviewListKey = ['review-list', { submissionContextId, submissionId }];

  const shortname = useCurrentOrgId();
  const { data: organization } = useQuery({
    queryKey: [`organization-${shortname}`],
    queryFn: () => (shortname ? fetchOrganization(shortname) : null),
    retry: 0
  });

  const organizationId = organization?.id ?? '';

  const inviteReviewerMutation = useInviteReviewer(
    submissionId,
    submissionContextId,
    reviewListKey,
    organizationId
  );

  const requestReviewInRevisionMutation = useRequestReviewInRevision(
    submissionId,
    submissionContextId,
    reviewListKey,
    organizationId
  );

  const steps = [AddReviewerWithSearch, ReviewerInvitationEmailSelection];

  const emailContentMethods = useForm<EmailContentForm>({
    defaultValues: {
      emailContent: {
        body: '',
        subject: '',
        recipients: { to: [], cc: [], bcc: [] },
        attachments: []
      }
    },
    resolver: zodResolver(emailContentSchema)
  });

  const emailAttachments = emailContentMethods.watch(
    'emailContent.attachments'
  );

  const isUploadingAttachments = useMemo(() => {
    return !!emailAttachments?.some(att => att.isUploading);
  }, [emailAttachments]);

  const commandName = availableActions.contains('RequestReviewInRevision')
    ? 'RequestReviewInRevision'
    : 'RequestReview';

  const { data: emailTemplates, isLoading: isLoadingTemplates } = useQuery({
    queryKey: [
      'browse-email-templates',
      organizationId,
      commandName,
      submissionContextId
    ],
    queryFn: () =>
      organizationId
        ? getAllTemplatesByCommand({
            organizationId,
            commandName,
            query: {
              submissionContextId,
              ...(['RequestReview', 'RequestReviewInRevision'].includes(
                commandName
              )
                ? { subtype: 'manual' }
                : {})
            }
          })
        : null,
    enabled: !!organizationId
  });

  const Component = steps[currentStep - 1];

  if (!Component) return null;

  const goToNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const goToPreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleCloseModal = () => {
    closeModal();
  };

  const handleExit = () => {
    setShowExitConfirmModal(false);
    closeModal();
  };

  const { sendReviewerInvitations, isProcessing } = useSendReviewerInvitations({
    ensureUser,
    availableActions,
    inviteReviewerMutation,
    requestReviewInRevisionMutation,
    toast
  });

  const convertFormEmailToCommand = (form: IEmailFormType): IEmailCommand => {
    return {
      subject: form.subject,
      body: form.body,
      recipients: normalizeRecipients(form.recipients),
      attachments: form.attachments
    };
  };

  const handleInviteReviewers = async () => {
    const { getValues, trigger } = emailContentMethods;
    const baseEmailContext = convertFormEmailToCommand(
      getValues('emailContent')
    );

    const isValid = await trigger();
    if (!isValid) {
      scrollToFirstError();
      return;
    }

    const reviewerEmails = confirmedReviewers.map(r => r.email);

    sendReviewerInvitations(reviewerEmails, baseEmailContext, () => {
      setShowConfirmModal(false);
      goToPreviousStep();
      markReviewersInvited(reviewerEmails);
    });
  };

  const reviewerInvitationNoTemplateSelection = () => {
    const data = emailTemplates?.data;
    if (!data || data.length === 0) {
      toast({
        variant: 'error',
        title: 'Missing Email Template',
        description:
          'Unable to send invitations because no email template exists.',
        isClosable: true
      });
      return;
    }

    const defaultIndex = data.findIndex(t => t.default);
    const index = defaultIndex === -1 ? 0 : defaultIndex;

    const template = data[index];

    if (!template) {
      toast({
        variant: 'error',
        title: 'Missing Email Template',
        description:
          'Unable to send invitations because no email template exists.',
        isClosable: true
      });
      return;
    }

    const baseEmailContext: IEmailCommand = {
      subject: template.subject,
      body: template.body,
      recipients: {
        to: template.recipients.to,
        cc: template.recipients.cc,
        bcc: template.recipients.bcc
      }
    };

    const reviewerEmails = confirmedReviewers.map(r => r.email);

    sendReviewerInvitations(reviewerEmails, baseEmailContext, () => {
      setShowConfirmModal(false);
      markReviewersInvited(reviewerEmails);
    });
  };

  const handleStepRender = () => {
    const Component = steps[currentStep - 1];

    if (!Component) return null;

    switch (currentStep) {
      case 1:
        return (
          <Component
            goToPreviousStep={goToPreviousStep}
            accountCreationEnabled={isReviewerAccountCreationEnabled}
            closeModal={handleCloseModal}
            blockedReviewers={blockedReviewers}
            isLoadingTemplates={isLoadingTemplates}
            emailTemplates={emailTemplates?.data}
          />
        );
      case 2:
        return (
          <FormProvider {...emailContentMethods}>
            <Component
              goToPreviousStep={goToPreviousStep}
              accountCreationEnabled={isReviewerAccountCreationEnabled}
              closeModal={handleCloseModal}
              blockedReviewers={blockedReviewers}
              isLoadingTemplates={isLoadingTemplates}
              emailTemplates={emailTemplates?.data}
            />
          </FormProvider>
        );
      default:
        return null;
    }
  };

  return (
    <Overlay>
      {showConfirmModal && (
        <ConfirmInviteModal
          isProcessing={isProcessing}
          onClose={() => setShowConfirmModal(false)}
          onInviteReviewers={reviewerInvitationNoTemplateSelection}
          option={'invite'}
        />
      )}
      {showExitConfirmModal && (
        <ConfirmInviteExitModal
          onClose={handleExit}
          onCancel={() => setShowExitConfirmModal(false)}
        />
      )}
      <NavbarDashboard />
      <Box
        backgroundColor="surfaceTertiaryFill"
        paddingY={2}
        minH="100vh"
        paddingX={4}
      >
        {handleStepRender()}
      </Box>
      <MultiStepFooter
        isDisabled={!confirmedReviewers.length}
        currentStep={currentStep}
        totalSteps={steps.length}
        goToNextStep={goToNextStep}
        inviteReviewers={() =>
          currentStep < steps.length
            ? setShowConfirmModal(true)
            : handleInviteReviewers()
        }
        isProcessing={isProcessing}
        handleCloseModal={handleCloseModal}
        isAttachmentUploading={isUploadingAttachments}
      />
    </Overlay>
  );
};

export default RsInviteModal;
